import { BaseViewStore } from "core/stores";
import { observable, action, runInAction, when } from "mobx";
import applicationContext from "core/utils/applicationContext";
import _ from "lodash";
import { BaasicDropdownStore } from "core/stores";
import { uuid, nikoSubCategoriesSort } from "core/utils";
import { ModalParams } from "core/models";
import { LookupService } from "common/services";

@applicationContext
class NikoPatientTemplateListViewStore extends BaseViewStore {
  @observable accessor productStore;
  @observable accessor activeTemplates;
  @observable accessor inactiveTemplates;
  @observable accessor isDropdownOpen;
  @observable accessor disabledItem;
  @observable accessor showInactiveTemplates = false;
  @observable accessor templateExpanded = { id: null, expanded: false };

  constructor(rootStore, componentsProps) {
    super(rootStore);

    this.rootStore = rootStore;

    this.pageViewStore = componentsProps.pageViewStore;
    this.productStore = new BaasicDropdownStore({ filterable: true, textField: "itemId" }, { fetchFunc: async () => [] });
    this.lookupService = new LookupService(rootStore.application.baasic.apiClient);

    this.templateChangeConfirmModal = new ModalParams({ onClose: null });
    this.templateChangeConfirmModal.onConfirm = async () => { this.templateChangeConfirmModal.close(); };
    this.templateChangeConfirmModal.yesLabel = 'MODAL.ACKNOWLEDGE';
    this.templateChangeConfirmModal.doNotShowCancel = true;

    this.pageName = 'pm';

    this.onDocumentClick = (e) => {
      if (!this.templateExpanded.id) return;
      const elements = [
        ...document.querySelectorAll(`[id="templateQuantity-${this.templateExpanded.id}"]`),
        ...document.querySelectorAll(`[id="orderQuantity-${this.templateExpanded.id}"]`),
      ];
      const clickedOutside = elements.length === 0 || elements.every((el) => !el.contains(e.target));
      if (clickedOutside) {
        runInAction(() => {
          this.templateExpanded = { id: null, expanded: false };
        });
      }
    };
    document.addEventListener('click', this.onDocumentClick);

    this.fetch([this.initialize()]);
  }

  destroy() {
    document.removeEventListener('click', this.onDocumentClick);
    super.destroy();
  }

  @action.bound
  async initialize() {
    await this.getLookups();
    await this.getTemplates();
    runInAction(() => {
      this.showInactiveTemplates = this.pageViewStore.nikoPatientDetail.status == 'Discharged' || this.pageViewStore.nikoPatientDetail.status == 'Expired';
    });
  }

  @action.bound
  async getTemplates() {
    try {
      const response = await this.pageViewStore.patientTemplateService.find({
        nikoPatientDetailId: this.pageViewStore.nikoPatientDetail.id,
        embed: "ClinicalStatus,SubCategory,PatientTemplateDiagnosisCodes,OrderingDoctor,PatientTemplateMissingDocuments,ItemCategory,PatientTemplateItems",
        orderBy: "DateCreated",
        pageNumber: 1,
        pageSize: 10000
      });

      const items = nikoSubCategoriesSort(response && response.data && response.data.item ? response.data.item : []);
      const missingDocumentLookup = (await this.pageViewStore.missingDocumentService.find({ rpp: 100, isActive: true, sort: "name|asc" })).data.item;
      items.forEach(element => {
        element.history = { userName: '', eventDate: null, fieldName: '' };
        element.patientTemplateItems && element.patientTemplateItems.forEach(item => {
          item.savedQuantity = item.quantity;
          item.savedSalesOrderQuantity = item.salesOrderQuantity;
          item.savedNote = item.note;
          item.history = { userName: '', eventDate: null, fieldName: '' };
        });
        element.isDifferentQty = element.patientTemplateItems && element.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
        element.missingDocumentStore = new BaasicDropdownStore(
          { multi: true, filterable: true, dataItemKey: "id" },
          {
            fetchFunc: async (term = "") => {
              const filteredData = missingDocumentLookup.filter(mc => (mc.itemCategoryId === element.subCategory.itemCategoryId || !mc.itemCategoryId) && mc.name.toLowerCase().includes(term.toLowerCase()));
              let filteredMissingDocuments = await this.filterMissingDocumentStore(filteredData, element);
              let mappedResult = _.map(filteredMissingDocuments, (item) => {
                return _.extend(item, { missingDocuments: item.id });
              });
              return mappedResult;
            }
          }
        )
      });
      const activeItems = items.filter(i => !i.isDiscontinued && (this.pageViewStore.nikoPatientDetail.status == 'Active' || this.pageViewStore.nikoPatientDetail.status == 'Hospital'));

      const inactiveItems = _.orderBy(
        items.filter(i =>
          i.isDiscontinued ||
          this.pageViewStore.nikoPatientDetail.status === "Discharged" ||
          this.pageViewStore.nikoPatientDetail.status === "Expired"
        ),
        [
          i => (i.isDiscontinued ? 1 : 0),
          i => i.isDiscontinued ? new Date(i.dateDiscontinued) : null
        ],
        ["asc", "desc"]
      );

      runInAction(() => {
        this.activeTemplates = activeItems;
        this.inactiveTemplates = inactiveItems;
      });
    }
    catch (err) {
      return this.rootStore.notificationStore.error(err.data ? err.data : err.message);
    }
  }

  @action.bound
  async getLookups() {
    this.insertImportActionId = this.pageViewStore.insertActionId;
    this.deleteImportActionId = this.pageViewStore.deleteActionId;
    this.updateImportActionId = this.pageViewStore.updateActionId;
    this.syringeDefaultItemTypeId = this.pageViewStore.syringeDefaultItemTypeId;
  }

  @action.bound
  async fetchPatientTemplateItemsById(patientTemplateId) {
    let patientTemplate = this.activeTemplates.find(x => x.id == patientTemplateId);
    if (!patientTemplate) {
      patientTemplate = this.inactiveTemplates.findIndex(x => x.id == patientTemplateId);
    }

    if (patientTemplate && patientTemplate.patientTemplateItems) {
      let responseData = (await this.pageViewStore.patientTemplateService.get(patientTemplateId, { embed: 'PatientTemplateItems' })).data;
      if (responseData.patientTemplateItems) {
        runInAction(() => {
          Object.assign(patientTemplate.patientTemplateItems, responseData.patientTemplateItems);
          patientTemplate.patientTemplateItems.forEach(item => {
            item.savedQuantity = item.quantity;
            item.savedSalesOrderQuantity = item.salesOrderQuantity;
            item.savedNote = item.note;
            item.history = item.history || { userName: '', eventDate: null, fieldName: '' };
          });
          patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
        });
      }
    }
  }

  @action.bound
  async fetchPatientTemplateIntakeStatusById(patientTemplate) {
    if (!patientTemplate) {
      return;
    }
    let responseData = (await this.pageViewStore.patientTemplateService.get(patientTemplate.id, { fileds: 'IntakeStatusId' })).data;
    if (patientTemplate.intakeStatusId != responseData.intakeStatusId) {
      patientTemplate.intakeStatusId = responseData.intakeStatusId;
    }
  }

  @action.bound
  async openNikoTemplateCreateModal() {
    await this.pageViewStore.nikoTemplateCreateModal.open({
      nikoPatientDetail: this.pageViewStore.nikoPatientDetail,
      fetchPatientTemplates: () => this.getTemplates(),
      pageName: this.pageName
    });
  }

  @action.bound
  async changeDiscontinuedStatus(patientTemplate) {
    await this.pageViewStore.patientTemplateService.updateDiscontinuedStatus({ id: patientTemplate.id, isDiscontinued: !patientTemplate.isDiscontinued });
    await this.getTemplates()
  }

  @action.bound
  async openPatientTemplateMissingProductNoteModal(patientTemplate) {
    this.pageViewStore.nikoTemplateMissingProductNoteModal.open({
      patientTemplate: patientTemplate,
    });
  }

  @action.bound
  async openFacilityOrderTemplateNewFlowDeleteModal(patientTemplate) {
    this.pageViewStore.facilityOrderTemplateNewFlowDeleteModal.open({
      item: patientTemplate,
      deleteTemplate: () => this.deleteTemplate(patientTemplate.id),
      numberOfTemplates: () => this.getNumberOfTemplates()
    });
  }

  @action.bound
  getNumberOfTemplates() {
    return (this.activeTemplates || []).length + (this.inactiveTemplates || []).length;
  }

  @action.bound
  async deleteTemplate(patientTemplateId) {
    this.pageViewStore.disabled = true;
    this.disabledItem = true;
    try {
      const relatedEntity = [...this.activeTemplates, ...this.inactiveTemplates].find(x => x.templateId === patientTemplateId);
      if (relatedEntity) {
        await this.pageViewStore.patientTemplateService.delete({ id: relatedEntity.id });
        this.removeTemplate(relatedEntity.id);
      }

      await this.pageViewStore.patientTemplateService.delete({ id: patientTemplateId });
      this.removeTemplate(patientTemplateId);
      this.rootStore.notificationStore.success("Template successfully deleted.");
    } catch (err) {
      this.rootStore.notificationStore.error("Error occurred while trying to delete a template.");
    } finally {
      this.pageViewStore.disabled = false;
      this.disabledItem = false;
    }
  }

  @action.bound
  removeTemplate(patientTemplateId) {
    let id = this.activeTemplates.findIndex((t) => t.id === patientTemplateId);
    if (id > -1) return this.activeTemplates.splice(id, 1);

    id = this.inactiveTemplates.findIndex((t) => t.id === patientTemplateId);
    if (id > -1) return this.inactiveTemplates.splice(id, 1);
  }

  @action.bound
  setItemToggle(patientTemplateItem) {
    patientTemplateItem.toggle = false;
  }

  @action.bound
  async saveItemOnProductSelect(patientTemplateItem, product) {
    this.pageViewStore.disabled = true;
    this.disabledItem = true;

    const template = [...this.activeTemplates, ...this.inactiveTemplates].find((t) => t.id === patientTemplateItem.patientTemplateId);
    if (!template.isNew && template.subCategory.abrv === 'Trach-Tube' && template.patientTemplateItems.some(x => x.productId && x.actionId !== this.deleteImportActionId)) {
      this.rootStore.modalStore.showConfirm('FACILITY_ORDER_TEMPLATE.EDIT.CONFIRM_DELTE_TRACH_TUBE_ITEMS', async () => { await this.saveItem(patientTemplateItem, product, template); });
      this.pageViewStore.disabled = false;
      this.disabledItem = false;
    } else {
      await this.saveItem(patientTemplateItem, product, template);
    }
  }

  @action.bound
  async saveItem(patientTemplateItem, product, template) {
    try {
      if (!template) return;

      const isEnteral = template.itemCategory && template.itemCategory.abrv === "enteral";
      const items = await this.getAdditionalItems(template.patientTemplateItems, product, template.subCategoryId, isEnteral, template, patientTemplateItem.patientTemplateId);

      _.each(items, async item => {
        let updatepatientTemplateItem = template.patientTemplateItems.find(x => x.id === item.id);
        if (updatepatientTemplateItem) {
          updatepatientTemplateItem.procedureCode = item.procedureCode;
        }
        else {
          const res = await this.pageViewStore.patientTemplateItemService.create({
            id: item.id,
            product: item.product,
            productId: item.product.id,
            patientTemplateId: patientTemplateItem.patientTemplateId,
            quantity: item.quantity || 0,
            salesOrderQuantity: item.quantity || 0,
            note: item.note || null,
            procedureCode: item.procedureCode,
          });
          const created = { ...res.data, product: item.product };
          runInAction(() => {
            const pos = template.patientTemplateItems.findIndex((i) => patientTemplateItem === i || patientTemplateItem.id === i.id);
            if (pos > -1) {
              template.patientTemplateItems.splice(pos, 1, created);
            }
            else {
              template.patientTemplateItems.push(created);
            }
            template.isDifferentQty = template.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
          });
          const isEnteral = template.itemCategory && template.itemCategory.abrv === "enteral";
          const isFormula = (item.product.itemType && (item.product.itemType.abrv === "bottle" || item.product.itemType.abrv === "can"))
            || (item.product.procedureCode_ && item.product.procedureCode_.abrv === "B4155");
          if (isEnteral && isFormula) {
            this.openNikoPatientTemplateItemCalculationModal(template.patientTemplateItems.find(e => e.id === created.id), template);
          }
        }
      });
      if (template.subCategory.abrv === 'Trach-Tube') {
        await this.getTemplates();
      }
      await template.missingDocumentStore.filterAsync();
      await this.fetchPatientTemplateIntakeStatusById(template);
    } catch (err) {
      this.rootStore.notificationStore.error(err.data ? err.data : "Error while adding additional items");
    } finally {
      this.pageViewStore.disabled = false;
      this.disabledItem = false;
    }
  }

  @action.bound
  async openPatientTemplateDoNotSupplyModal(patientTemplate) {
    this.pageViewStore.patientTemplateDoNotSupplyModal.open({
      icon: "over-circle",
      title: `${patientTemplate.isUserDns ? "Unmark" : "Mark"} As Do Not Supply`,
      description: patientTemplate.isUserDns ? "Are you sure you want to unmark this order as Do Not Supply?" : undefined,
      action: () => this.changeSystemDnsStatus(patientTemplate)
    });
  }

  @action.bound
  async changeSystemDnsStatus(patientTemplate) {
    patientTemplate.isUserDns = !patientTemplate.isUserDns;
    await this.pageViewStore.patientTemplateService.update(patientTemplate);
    await this.getTemplates()
  }

  @action.bound
  async getAdditionalItems(patientTemplateItems, product, subCategoryId, isEnteral, template) {
    let tempItem = {
      product: product,
      productId: product.id,
      procedureCode: product.procedureCode_ && product.procedureCode_.abrv
    };

    const isFormula = (product.itemType && (product.itemType.abrv === "bottle" || product.itemType.abrv === "can")) || (product.procedureCode_ && product.procedureCode_.abrv === "B4155");
    if (isEnteral && isFormula) {
      _.extend(tempItem, { itemMls: 1, itemCals: product.calsPerMl });
    }

    const response = (await this.pageViewStore.patientTemplateItemService.createAdditionalItems(
      {
        patientTemplateItem: tempItem,
        patientTemplateItems: patientTemplateItems.filter((i) => i.product).map((i) => { return { id: i.id, product: i.product, productId: i.productId, procedureCode: i.procedureCode, importActionId: i.importActionId }; }),
        facilityId: this.pageViewStore.nikoPatientDetail.facilityId,
        subCategoryId: subCategoryId,
        clinicalStatusId: template.clinicalStatusId,
        patientTemplateId: template.id,
        pageName: this.pageName
      }
    )).data;
    return response;
  }

  @action.bound
  showItemQuantities(patientTemplate, show) {
    this.templateExpanded = { id: patientTemplate.id, expanded: show };
  }

  @action.bound
  editItem(patientTemplateItem, value, prop) {
    if (prop === "quantity") {
      patientTemplateItem.quantity = parseInt(value);
      patientTemplateItem.salesOrderQuantity = parseInt(value);
    } else if (prop === "salesOrderQuantity") {
      patientTemplateItem.salesOrderQuantity = parseInt(value);
    } else {
      patientTemplateItem[prop] = value;
    }
  }

  @action.bound
  async updateItem(patientTemplateItem, patientTemplate) {
    if (Number.isNaN(patientTemplateItem.quantity) || patientTemplateItem.quantity === '' || patientTemplateItem.quantity === null || patientTemplateItem.quantity === undefined) {
      patientTemplateItem.quantity = 0;
    }

    if (Number.isNaN(patientTemplateItem.salesOrderQuantity) || patientTemplateItem.salesOrderQuantity === '' || patientTemplateItem.salesOrderQuantity === null || patientTemplateItem.salesOrderQuantity === undefined) {
      patientTemplateItem.salesOrderQuantity = 0;
    }

    if (patientTemplateItem.quantity == patientTemplateItem.savedQuantity &&
        patientTemplateItem.salesOrderQuantity == patientTemplateItem.savedSalesOrderQuantity &&
        patientTemplateItem.note == patientTemplateItem.savedNote) {
      return;
    }

    this.pageViewStore.disabled = true;
    this.disabledItem = true;
    await this.pageViewStore.patientTemplateItemService.update(patientTemplateItem.id, {
      productId: patientTemplateItem.productId,
      patientTemplateId: patientTemplateItem.patientTemplateId,
      quantity: patientTemplateItem.quantity,
      salesOrderQuantity: patientTemplateItem.salesOrderQuantity,
      note: patientTemplateItem.note,
      procedureCode: patientTemplateItem.procedureCode,
      mlph: patientTemplateItem.mlph,
      itemMls: patientTemplateItem.itemMls,
      itemCals: patientTemplateItem.itemCals,
      frequency: patientTemplateItem.frequency,
      actionId: patientTemplateItem.actionId == this.pageViewStore.insertActionId || patientTemplateItem.actionId == this.pageViewStore.deleteActionId ? patientTemplateItem.actionId : this.pageViewStore.updateActionId
    });

    patientTemplateItem.savedQuantity = patientTemplateItem.quantity;
    patientTemplateItem.savedSalesOrderQuantity = patientTemplateItem.salesOrderQuantity;
    patientTemplateItem.savedNote = patientTemplateItem.note;
    if (patientTemplate) {
      patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
    }
    await this.fetchPatientTemplateIntakeStatusById(patientTemplate);

    this.pageViewStore.disabled = false;
    this.disabledItem = false;
  }

  @action.bound
  async checkItemDelete(patientTemplate, patientTemplateItem) {
    if (patientTemplate.patientTemplateItems.length == 1) {
      this.pageViewStore.facilityOrderTemplateItemNewFlowDeleteQuestionModal.open({
        deleteItem: () => this.deleteItem(patientTemplate, patientTemplateItem, false),
        deletePatient: () => this.deleteItem(patientTemplate, patientTemplateItem, true),
        isNikoHealthModal: true
      });
    }
    else {
      this.deleteItem(patientTemplate, patientTemplateItem);
      this.pageViewStore.disabled = false;
      this.disabledItem = false;
    }
  }

  @action.bound
  async deletePatientTemplateItem(patientTemplate, patientTemplateItem, deleteTemplate) {
    try {
      this.pageViewStore.disabled = true;
      if (this.pageViewStore.nikoPatientDetail.isNew && deleteTemplate) {
        return this.checkItemDelete(patientTemplate, patientTemplateItem);
      }

      if (patientTemplateItem.actionId == this.insertImportActionId) {
        await this.deleteItem(patientTemplate, patientTemplateItem);
      }
      else {
        await this.setAction(patientTemplateItem, this.deleteImportActionId, patientTemplate);
      }
    }
    catch (err) {
      return this.rootStore.notificationStore.error(err.data ? err.data : err.message);
    } finally {
      this.pageViewStore.disabled = false;
      this.disabledItem = false;
    }
  }

  @action.bound
  async deleteItem(patientTemplate, patientTemplateItem, deleteTemplate) {
    try {
      this.pageViewStore.disabled = true;
      this.disabledItem = true;

      if (deleteTemplate && patientTemplate.patientTemplateItems.length === 1) {
        return await this.deleteTemplate(patientTemplate.id);
      }

      if (!patientTemplateItem.id || !patientTemplateItem.productId) {
        runInAction(() => {
          patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems.filter((i) => i !== patientTemplateItem);
          patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
        });
      }
      else {
        const procedureCode = patientTemplateItem.product && patientTemplateItem.product.procedureCode_ && patientTemplateItem.product.procedureCode_.abrv;
        const hasSyringeItem = patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => x.product && x.product.defaultItemTypeId == this.syringeDefaultItemTypeId);

        await this.pageViewStore.patientTemplateItemService.delete(patientTemplateItem.id, { setProcedureCode: (procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem });
        this.pageViewStore.disabled = false; //added bacause of edge case where input wouldn't enable on specific user 
        this.disabledItem = false;
        runInAction(() => {
          patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems.filter((i) => i.id !== patientTemplateItem.id);
          patientTemplate.isDifferentQty = patientTemplate.patientTemplateItems.some(x => x.quantity != x.salesOrderQuantity);
        });
        if ((procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem) {
          await this.fetchPatientTemplateItemsById(patientTemplate.id);
        }
        await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
      }

      this.pageViewStore.facilityOrderTemplateItemNewFlowDeleteQuestionModal.close();
      await patientTemplate.missingDocumentStore.filterAsync();
    } catch (err) {
      this.rootStore.notificationStore.error(err.data ? err.data : err.message);
    } finally {
      this.pageViewStore.disabled = false;
      this.disabledItem = false;
    }
  }

  @action.bound
  async addEmptyItem(patientTemplate) {
    if (patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some((i) => !i.productId)) return;

    if (!patientTemplate.productStore) {
      patientTemplate.productStore = await this.setProductStore(patientTemplate.subCategoryId);
    }
    patientTemplate.patientTemplateItems = patientTemplate.patientTemplateItems || [];

    patientTemplate.patientTemplateItems.push({
      id: uuid(),
      product: null,
      productId: null,
      quantity: 0,
      salesOrderQuantity: 0,
      note: null,
      patientTemplateId: patientTemplate.id,
      toggle: true,
      actionId: this.insertImportActionId,
    });

    this.isDropdownOpen = true;
  }

  @action.bound
  async setProductStore(subCategoryId) {
    const productStore = new BaasicDropdownStore(
      {
        filterable: true,
        textField: "itemId",
      },
      {
        fetchFunc: async (term) => {
          if ((term && term.trim()) === "") term = undefined;

          let params = {
            searchQuery: term,
            rpp: 50,
            subCategoryId: subCategoryId,
            isActive: true,
            embed: "Product,Product.ItemType,Product.ItemCategory,Product.SubCategory,Product.UnitOfMeasure,Product.ProcedureCode_",
          };

          const response = await this.pageViewStore.itemSubcategoryService.find(params);
          let filteredProducts = response.data.item.filter((x) => !x.product.itemId.toLowerCase().includes("-cs"));
          return _.map(filteredProducts, (item) => {
            return { ...item.product };
          });
        },
      }
    );
    this.productStore = productStore;
    return productStore;
  }

  @action.bound
  changeInactiveTemplatesVisibility() {
    this.showInactiveTemplates = !this.showInactiveTemplates;
  }

  @action.bound
  openNikoPatientTemplateItemCalculationModal(patientTemplateItem, patientTemplate) {
    this.pageViewStore.nikoPatientTemplateItemCalculationModal.open({
      patientTemplateItem: patientTemplateItem,
      facilityId: this.pageViewStore.nikoPatientDetail.facilityId,
      patientTemplateItemService: this.pageViewStore.patientTemplateItemService,
      updateItem: async (patientTemplateItem) => {
        await this.updateItem(patientTemplateItem, patientTemplate);
        await this.fetchPatientTemplateItemsById(patientTemplate.id);
        await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
      }
    });
  }

  async filterMissingDocumentStore(missingDocuments, patientTemplate) {
    let result = missingDocuments;
    if (missingDocuments && missingDocuments.some(m => m)) {
      let template = this.activeTemplates ? this.activeTemplates.find((t) => t.id === patientTemplate.id) : patientTemplate;
      if (!template) template = this.inactiveTemplates.find((t) => t.id === patientTemplate.id);
      if (!template) return;

      const patientTemplateItems = template.patientTemplateItems;

      const diabeticFormulaSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "diabetic-formula").id;
      const renalFormulaNeproNRSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "renal-formula-nepro-nr").id;
      const renalFormulaSuplenaSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "renal-formula-suplena").id;
      const pulmonaryFormulaSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "pulmonary-formula").id;
      const elementalFormulaSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "elemental-formula").id;
      const proteinSupplementSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "protein-supplement").id;
      const siliconeCatheterSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "silicone-catheter").id;
      const coudeCatheterSpecialtyTypeId = this.pageViewStore.specialtyTypeLookup.find(x => x.abrv === "coude-catheter").id;

      if (patientTemplateItems.length > 0)
        await when(() => patientTemplateItems.every(item => item.product !== undefined && item.product !== null));

      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == diabeticFormulaSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-diabetic");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == renalFormulaNeproNRSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-renal-nepro");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == renalFormulaSuplenaSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-renal-suplena");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == pulmonaryFormulaSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-pulmonary");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == elementalFormulaSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-elemental");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId === proteinSupplementSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-protein-supplement");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == siliconeCatheterSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-silicone-catheter");
      }
      if (!(patientTemplateItems && patientTemplateItems.some(i => i.product && i.product.specialtyTypeId == coudeCatheterSpecialtyTypeId))) {
        result = result.filter(i => i.abrv !== "specialty-product-justification-coude-catheter");
      }
    }
    return result;
  }

  @action.bound
  async swapPatientTemplateItem(patientTemplateItem, patientTemplate) {
    patientTemplate.disabled = true;
    try {
      if (patientTemplateItem.actionId === this.deleteImportActionId) {
        await this.setAction(patientTemplateItem, this.updateImportActionId, patientTemplate)
      }
      else {
        await this.deletePatientTemplateItem(patientTemplate, patientTemplateItem, false)
      }
      this.addEmptyItem(patientTemplate);
    }
    catch (err) {
      this.rootStore.notificationStore.error("FACILITY_ORDER_TEMPLATE_ITEM.EDIT.DELETE_ERROR");
    } finally {
      this.pageViewStore.disabled = false;
      this.disabledItem = false;

      patientTemplate.disabled = false;
    }
  }

  @action.bound
  async setAction(patientTemplateItem, actionId, patientTemplate) {
    patientTemplateItem.actionId = actionId;
    const procedureCode = patientTemplateItem.product && patientTemplateItem.product.procedureCode_ && patientTemplateItem.product.procedureCode_.abrv;
    const hasSyringeItem = patientTemplate.patientTemplateItems && patientTemplate.patientTemplateItems.some(x => x.product && x.product.defaultItemTypeId == this.syringeDefaultItemTypeId);
    await this.pageViewStore.patientTemplateItemService.update(patientTemplateItem.id, { ...patientTemplateItem, setProcedureCode: (procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem });
    if ((procedureCode == 'B4035' || procedureCode == 'B4036') && hasSyringeItem) {
      await this.fetchPatientTemplateItemsById(patientTemplate.id);
    }
    await this.fetchPatientTemplateIntakeStatusById(patientTemplate);
  }

  @action.bound
  async getFieldHistory(item, fieldName, value) {
    await this.pageViewStore.getFieldHistory(item, fieldName, value);
  }
}

export default NikoPatientTemplateListViewStore;
